import { useState, useEffect } from "react";
import { usePost } from "../hooks/usePost";
import { FileOptionTabTitle } from "../types/fileUploadTypes";
import { toSlug as slugify } from "../utils/formattingUtils";
import { Filter as Option } from "../types/PageFilterTypes";
import {
  CommonDataItem,
  CommonDataRequestType,
  DistinctFundsRequest,
  FundsWManagerItem,
  ManagerItem,
} from "../types/CommonDataRequestTypes";
import { useToast } from "../store/ToastProvider";
import { MESSAGES } from "../messages";
import { JobParam, JobParameterName, JobType } from "../models/JobModels";
import { DEFAULT_LOADING_OPTIONS } from "../utils/PageFilterUtils";
import { useUser } from "../store/UserProvider";
import { UserRole, CognitoClaim } from "../models/userModels";

// Define the parameters for each job type
export const JOB_PARAMETERS: Record<JobType, JobParameterName[]> = {
  [FileOptionTabTitle.PORTFOLIO]: [],
  [FileOptionTabTitle.VOTING_RECORDS]: [JobParameterName.FUNDS],
  [FileOptionTabTitle.ENGAGEMENT_RECORDS]: [JobParameterName.MANAGERS],
};

export const useJobParams = (type: string | null): UseJobParams => {
  const { addToast } = useToast();
  const { user } = useUser();
  const isUploadManager = user?.[CognitoClaim.ROLE] === UserRole.MANAGER_UPLOAD;
  const managerProfile = user?.managerProfile;

  const [jobParamsOptions, setJobParamsOptions] = useState<Option[][]>([[]]);
  const [jobParams, setJobParams] = useState<JobParam[]>([]);
  const [error, setError] = useState<boolean>(false);

  // Get the job type based on the fileType URL parameter
  const typeName: FileOptionTabTitle =
    Object.values(FileOptionTabTitle).find((key) => slugify(key) === type) ||
    FileOptionTabTitle.PORTFOLIO;

  const jobParamNames = JOB_PARAMETERS[typeName] || [];

  // Parameter Data fetching queries:
  const {
    error: distinctFundsError,
    sendData: fetchDistinctFunds,
    isLoading: isDistinctFundsLoadings,
  } = usePost<FundsWManagerItem[], CommonDataItem<DistinctFundsRequest>>();

  const {
    error: managerError,
    sendData: fetchManagers,
    isLoading: isManagerLoading,
  } = usePost<ManagerItem[], null>();

  // Check for loading
  const isLoading = isDistinctFundsLoadings || isManagerLoading;

  // Fetch job parameters based on the job type
  useEffect(() => {
    if (typeName) {
      (async (): Promise<void> => {
        await setJobParamsFromJobType();
      })();
    }
  }, [typeName]);

  // Handle error when fetching parameters
  useEffect(() => {
    if (distinctFundsError || managerError) {
      setError(true);
      addToast(MESSAGES.JOB_PARAMS.FETCHING_PARAMS_ERROR, "error");
    }
  }, [jobParamsOptions]);

  useEffect(() => {
    isLoading && setJobParamsOptions([DEFAULT_LOADING_OPTIONS]);
  }, [isLoading]);

  const setJobParamsFromJobType = async (): Promise<void> => {
    // Use Promise.all to wait for all asynchronous operations to complete
    const optionsArray = await Promise.all(
      jobParamNames.map(async (paramName) => {
        switch (paramName) {
          case JobParameterName.FUNDS:
            return await buildFundsOptions();

          case JobParameterName.MANAGERS:
            return await buildManagerOptions();
          default:
            return [];
        }
      }),
    );

    setJobParamsOptions(optionsArray);

    const autoSelected = optionsArray
      .filter((opts) => opts.length === 1)
      .map((opts) => ({
        parameterName: opts[0].selectorName as JobParameterName,
        value: opts[0].key as string,
      }));
    if (autoSelected.length > 0) setJobParams(autoSelected);
  };

  // Fetch distinct funds with managers
  const buildFundsOptions = async (): Promise<Option[]> => {
    // For upload managers, use their funds from profile
    if (isUploadManager && managerProfile?.funds) {
      return managerProfile.funds
        .filter((fund) => !fund.is_index)
        .map((fund) => ({
          key: fund.fund_id,
          displayValue: fund.fund_name,
          selectorName: JobParameterName.FUNDS,
        }));
    }

    // For regular users, fetch all funds
    const distinctFundsData = await fetchDistinctFunds("GET_COMMON_DATA", {
      name: CommonDataRequestType.DISTINCT_FUNDS_W_MANAGER,
      item: { distinct_funds: null },
    });
    if (!distinctFundsData) return [];
    return distinctFundsData
      .filter((fund) => !fund.is_index)
      .map((fund) => ({
        key: fund.fund_id,
        displayValue: fund.fund_name,
        selectorName: JobParameterName.FUNDS,
      }));
  };

  const buildManagerOptions = async (): Promise<Option[]> => {
    // For upload managers, use their profile as the only option
    if (isUploadManager && managerProfile) {
      return [
        {
          key: managerProfile.manager_id,
          displayValue: managerProfile.manager_name,
          selectorName: JobParameterName.MANAGERS,
        },
      ];
    }

    // For regular users, fetch all managers
    const managerData = await fetchManagers("GET_ALL_MANAGERS", null);
    if (!managerData) return [];
    return managerData.map((manager) => ({
      key: manager.manager_id,
      displayValue: manager.manager_name,
      selectorName: JobParameterName.MANAGERS,
    }));
  };

  const handleJobParamUpdate = (selectedParam: Option): void => {
    const selectedParamName: JobParameterName =
      selectedParam.selectorName as JobParameterName;
    const selectedParamObj: JobParam = {
      parameterName: selectedParamName,
      value: selectedParam.key as string,
    };
    const filteredParams = jobParams.filter((param) => {
      param.parameterName !== selectedParamName;
    });

    setJobParams([...filteredParams, selectedParamObj]);
  };

  return {
    jobParamsOptions,
    isLoading,
    error,
    handleJobParamUpdate,
    jobParams,
  };
};

type UseJobParams = {
  jobParamsOptions: Option[][];
  handleJobParamUpdate: (selectedParam: Option) => void;
  isLoading: boolean;
  error: boolean;
  jobParams: JobParam[];
};
